import React, { memo, useMemo } from 'react';
import { Stack, Typography } from 'investira.react.components';
import { HighchartsReact } from 'investira.react.charts';
import { formats } from 'investira.sdk';

const pvObterNumero = pValor => {
  if (pValor === null || pValor === undefined || pValor === '') {
    return null;
  }

  const xNumero = Number(pValor);
  return Number.isFinite(xNumero) ? xNumero : null;
};

const pvCalcularMedia = pValores => {
  const xValores = pValores.map(pvObterNumero).filter(pValor => pValor !== null);

  if (xValores.length === 0) {
    return null;
  }

  return xValores.reduce((pTotal, pValor) => pTotal + pValor, 0) / xValores.length;
};

export const calcularPerformancePorSessao = (pRespostas, pPerformanceMock = null) => {
  const xSessoes = (Array.isArray(pRespostas) ? pRespostas : []).reduce((pAcc, pResposta) => {
    const xSessaoId = pResposta?.avaliacao_secao_id;

    if (xSessaoId === null || xSessaoId === undefined) {
      return pAcc;
    }

    if (!pAcc[xSessaoId]) {
      pAcc[xSessaoId] = {
        id: xSessaoId,
        label: pResposta?.avaliacao_secao || 'Seção',
        notasGestor: [],
        notasEntidade: []
      };
    }

    pAcc[xSessaoId].notasGestor.push(pResposta?.nota);
    pAcc[xSessaoId].notasEntidade.push(pResposta?.revisor_nota);
    return pAcc;
  }, {});

  return Object.values(xSessoes).map((pSessao, pIndex) => ({
    id: pSessao.id,
    label: pSessao.label,
    notaGestor:
      pvObterNumero(pPerformanceMock?.notasGestor?.[pIndex]) ??
      pvCalcularMedia(pSessao.notasGestor),
    notaEntidade:
      pvObterNumero(pPerformanceMock?.notasEntidade?.[pIndex]) ??
      pvCalcularMedia(pSessao.notasEntidade)
  }));
};

const AvaliacoesOutputPerfomance = memo(props => {
  const { respostas, performanceMock } = props;
  const xSessoes = useMemo(
    () => calcularPerformancePorSessao(respostas, performanceMock),
    [performanceMock, respostas]
  );
  const xTemNotaGestor = xSessoes.some(pSessao => pSessao.notaGestor !== null);
  const xTemNotaEntidade = xSessoes.some(pSessao => pSessao.notaEntidade !== null);
  const xSeries = [];

  if (xTemNotaEntidade) {
    xSeries.push({
      name: 'Avaliação da entidade',
      data: xSessoes.map(pSessao => pSessao.notaEntidade),
      pointPlacement: 'on'
    });
  }

  if (xTemNotaGestor) {
    xSeries.push({
      name: 'Avaliação do gestor',
      data: xSessoes.map(pSessao => pSessao.notaGestor),
      pointPlacement: 'on'
    });
  }

  const xNotas = xSeries.flatMap(pSerie => pSerie.data).filter(pNota => pNota !== null);
  const xMinimo = xNotas.length > 0 ? Math.min(0, Math.floor(Math.min(...xNotas))) : 0;
  const xMaximo = xNotas.length > 0 ? Math.max(3, Math.ceil(Math.max(...xNotas))) : 3;

  const xOptions = {
    chart: {
      polar: true,
      type: 'line'
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: xSessoes.map(pSessao => pSessao.label),
      tickmarkPlacement: 'on',
      lineWidth: 0
    },
    yAxis: {
      gridLineInterpolation: 'polygon',
      lineWidth: 0,
      min: xMinimo,
      max: xMaximo,
      tickInterval: 1
    },
    tooltip: {
      shared: true,
      pointFormat:
        '<span style="color:{series.color}">● </span>{series.name}: <b>{point.y:.2f}</b><br/>'
    },
    series: xSeries,
    responsive: {
      rules: [
        {
          condition: {},
          chartOptions: {
            title: { x: 0 },
            legend: {
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'horizontal'
            },
            pane: { size: '80%' }
          }
        }
      ]
    }
  };

  return (
    <Stack flexGrow="1" gap={2}>
      <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
        <Stack mb={2}>
          <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 600 }}>
            Performance por seção
          </Typography>
        </Stack>

        {xSeries.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Nenhuma nota disponível para calcular a performance por seção.
          </Typography>
        ) : (
          <Stack direction="row" gap={2}>
            <Stack>
              <HighchartsReact options={xOptions} />
            </Stack>
            <Stack>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {xSessoes.map(pSessao => (
                  <Stack
                    key={pSessao.id}
                    mb={2}
                    sx={{
                      borderLeft: '8px solid',
                      borderColor:
                        pSessao.notaEntidade === null
                          ? 'divider'
                          : pSessao.notaEntidade < 0
                          ? 'error.main'
                          : pSessao.notaEntidade < 2
                          ? 'warning.main'
                          : 'primary.main',
                      px: 2
                    }}>
                    <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 600 }}>
                      {pSessao.label}
                    </Typography>
                    {pSessao.notaEntidade !== null && (
                      <Typography variant="caption" color="textPrimary">
                        Entidade: {formats.formatNumber(pSessao.notaEntidade, 2, true, false)}
                      </Typography>
                    )}
                    {pSessao.notaGestor !== null && (
                      <Typography variant="caption" color="textPrimary">
                        Gestor: {formats.formatNumber(pSessao.notaGestor, 2, true, false)}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});

export default AvaliacoesOutputPerfomance;
